import * as THREE from 'three'
import { type OctreeGeometry, OctreeGeometryNode } from '@/plugins/pointCloud/core/OctreeGeometry'
import type { PickParams, PickState, SignedDistanceField } from '@/plugins/pointCloud/core/types'
import { PointCloudMaterial } from '@/plugins/pointCloud/materials/PointCloudMaterial'
import type { ProfileRequest } from '@/plugins/pointCloud/ProfileRequest'
import { computeTransformedBoundingBox } from '@/plugins/pointCloud/utils/geometry-utils'
import { ClipTask, PointShape } from '@/shared/constants'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'
import { PointCloudTree, PointCloudTreeNode } from './PointCloudTree'

/** 更新单个视口 LOD 就绪度时使用的帧上下文。 */
export interface ViewLodProgressContext {
  /** 当前运行时帧号；省略时按单视口直接覆盖。 */
  frameNumber?: number
  /** 当前视口 id；省略时按单视口直接覆盖。 */
  viewId?: string
  /** 当前帧参与聚合的可见视口 id。 */
  visibleViewIds?: readonly string[]
}

/**
 * 点云八叉树节点类
 * @description 八叉树中的树节点，包装几何节点用于场景渲染
 */
export class PointCloudOctreeNode extends PointCloudTreeNode {
  /** 子节点数组（最多8个） */
  children: any[] = []
  /** THREE.js 场景节点 */
  sceneNode: THREE.Points | null = null
  /** 所属的八叉树 */
  octree: any = null
  /** 对应的几何节点 */
  geometryNode: OctreeGeometryNode | null = null
  /** 点云索引 */
  pcIndex?: number
  /** 所属的点云对象 */
  pointcloud?: PointCloudOctree
  /** 变换版本号 */
  _transformVersion?: number
  /** 包围盒节点 */
  boundingBoxNode?: any

  /**
   * 获取节点包含的点数量
   * @returns 点数量
   */
  getNumPoints(): number {
    return this.geometryNode?.numPoints ?? 0
  }

  /**
   * 检查节点是否已加载
   * @returns 几何载荷已驻留且场景节点仍有效时返回 true
   */
  isLoaded(): boolean {
    return this.geometryNode?.resourceState === 'resident' && this.sceneNode !== null
  }

  /**
   * 检查是否为树节点
   * @returns 总是返回 true
   */
  isTreeNode(): boolean {
    return true
  }

  /**
   * 检查是否为几何节点
   * @returns 总是返回 false
   */
  isGeometryNode(): boolean {
    return false
  }

  /**
   * 获取节点层级
   * @returns 层级值
   */
  getLevel(): number {
    return this.geometryNode?.level ?? 0
  }

  /**
   * 获取包围球
   * @returns 包围球对象
   */
  getBoundingSphere(): THREE.Sphere {
    return this.geometryNode?.boundingSphere ?? new THREE.Sphere()
  }

  /**
   * 获取包围盒
   * @returns 包围盒对象
   */
  getBoundingBox(): THREE.Box3 {
    return this.geometryNode?.boundingBox ?? new THREE.Box3()
  }

  /**
   * 获取点云的最小包围盒。
   *
   * @returns 以原点为 min、经偏移平移的 THREE.Box3
   */
  getMinimumBoundingBox() {
    const metadata = this.geometryNode?.octreeGeometry?.loader?.metadata ?? null
    const aPosition = metadata.attributes.find((a) => a.name === 'position')
    // octree
    // let min = new THREE.Vector3(...metadata.boundingBox.min);
    // let max = new THREE.Vector3(...metadata.boundingBox.max);
    const min = new THREE.Vector3(...aPosition.min)
    const max = new THREE.Vector3(...aPosition.max)
    const boundingBox = new THREE.Box3(min, max)
    const offset = min.clone()
    boundingBox.min.sub(offset)
    boundingBox.max.sub(offset)
    return boundingBox
  }

  /**
   * 获取当前节点的非空子节点列表。
   *
   * @returns 已存在的子节点数组，最多 8 个
   */
  getChildren() {
    const children: any[] = []

    for (let i = 0; i < 8; i++) {
      if (this.children[i]) {
        children.push(this.children[i])
      }
    }

    return children
  }

  /**
   * 获取落入指定变换盒内的点的世界坐标。
   *
   * @param boxNode - 单位立方体所在的场景节点，通过 matrixWorld 定义包围盒
   * @returns 命中点世界坐标数组，节点未加载时返回 null
   */
  getPointsInBox(boxNode) {
    if (!this.sceneNode) {
      return null
    }

    // @ts-expect-error 旧代码：OctreeGeometryNode 缺少 buffer 属性类型
    const buffer = this.geometryNode?.buffer

    const posOffset = buffer.offset('position')
    const stride = buffer.stride
    const view = new DataView(buffer.data)

    const worldToBox = boxNode.matrixWorld.clone().invert()
    const objectToBox = new THREE.Matrix4().multiplyMatrices(worldToBox, this.sceneNode.matrixWorld)

    const inBox: any[] = []

    const pos = new THREE.Vector4()
    for (let i = 0; i < buffer.numElements; i++) {
      const x = view.getFloat32(i * stride + posOffset + 0, true)
      const y = view.getFloat32(i * stride + posOffset + 4, true)
      const z = view.getFloat32(i * stride + posOffset + 8, true)

      pos.set(x, y, z, 1)
      pos.applyMatrix4(objectToBox)

      if (-0.5 < pos.x && pos.x < 0.5) {
        if (-0.5 < pos.y && pos.y < 0.5) {
          if (-0.5 < pos.z && pos.z < 0.5) {
            pos.set(x, y, z, 1).applyMatrix4(this.sceneNode.matrixWorld)
            inBox.push(new THREE.Vector3(pos.x, pos.y, pos.z))
          }
        }
      }
    }

    return inBox
  }

  /** 节点名称，代理自关联的几何节点 */
  get name() {
    return this.geometryNode?.name ?? ''
  }
}

/**
 * 实际点云对象
 * @classdesc 存储在 scenePointCloud 对象下，继承自 PointCloudTree
 * @description 这是 Potree 的核心类，表示一个基于八叉树的点云对象，支持渐进式加载和渲染
 */
export class PointCloudOctree extends PointCloudTree {
  /** 点预算（可渲染的最大点数） */
  pointBudget: number = Infinity
  /** 点云八叉树几何体 */
  pcoGeometry: OctreeGeometry
  /** 包围盒 */
  boundingBox: THREE.Box3
  /** 包围球 */
  boundingSphere: THREE.Sphere
  /** 点云材质 */
  material: PointCloudMaterial
  /** 目标可见点数 */
  visiblePointsTarget: number = 2 * 1000 * 1000
  /** 最小节点像素大小 */
  minimumNodePixelSize: number = 150
  /** 层级 */
  level: number = 0
  /** 是否显示包围盒 */
  showBoundingBox: boolean = false
  /** 包围盒节点列表 */
  boundingBoxNodes: any[] = []
  /** 加载队列 */
  loadQueue: any[] = []
  /** 可见区域包围盒 */
  visibleBounds: THREE.Box3 = new THREE.Box3()
  /** 可见节点列表 */
  visibleNodes: any[] = []
  /** 当前帧各可见视口的 LOD 就绪度快照。 */
  private _viewLodProgressByView = new Map<string, number>()
  /** LOD 就绪度快照所属帧号。 */
  private _viewLodProgressFrameNumber = -1
  /** 所有当前可见视口的保守聚合 LOD 就绪度。 */
  private _viewLodProgress = 0
  /** 是否生成 DEM（TODO: 审计是否可删除，UpdateVisibility 仍在读取） */
  generateDEM: boolean = false
  /** Profile 请求列表（TODO: 审计是否可删除，UpdateVisibility 仍在读取） */
  profileRequests: ProfileRequest[] = []
  /** 是否可见（内部状态） */
  _visible: boolean = true
  /** 投影信息 */
  projection?: any
  /** 后备投影 */
  fallbackProjection?: any
  /** 根节点 */
  root: any
  /** 可见节点纹理偏移映射 */
  visibleNodeTextureOffsets?: Map<any, number>
  /** 签名距离场（用于点拾取优化） */
  signedDistanceField?: SignedDistanceField
  /** Pick 状态对象 */
  pickState?: PickState
  /** 可见节点数量（外部补充属性） */
  numVisibleNodes?: number
  /** 可见点数量（外部补充属性） */
  numVisiblePoints?: number
  /** 最深可见层级（外部补充属性） */
  deepestVisibleLevel?: number
  /** 最大层级（外部补充属性） */
  maxLevel?: number
  /** DEM 对象（TODO: 审计是否可删除，UpdateVisibility 仍在读取） */
  dem?: any
  /** 屏幕高度 */
  screenHeight: number = 0
  /** 屏幕宽度 */
  screenWidth: number = 0
  /** 几何拓扑节点到可选渲染载荷节点的稳定映射。 */
  private renderNodes = new WeakMap<OctreeGeometryNode, PointCloudOctreeNode>()

  /**
   * 构造函数
   * @param geometry 八叉树几何体
   * @param material 点云材质（可选）
   */
  constructor(geometry: OctreeGeometry, material?: PointCloudMaterial | null) {
    super()

    this.pcoGeometry = geometry
    this.boundingBox = this.pcoGeometry.boundingBox ?? new THREE.Box3()
    this.boundingSphere =
      this.boundingBox.getBoundingSphere(new THREE.Sphere()) ?? new THREE.Sphere()
    this.material = material || new PointCloudMaterial()
    this.position.copy(geometry.offset ?? new THREE.Vector3())
    this.updateMatrix()

    {
      const priorityQueue = ['rgba', 'rgb', 'intensity', 'classification']
      let selected = 'rgba'

      for (const attributeName of priorityQueue) {
        const attribute = this.pcoGeometry?.pointAttributes?.attributes.find(
          (a) => a.name === attributeName
        )

        if (!attribute) {
          continue
        }

        const min: number[] = Array.isArray(attribute.range[0])
          ? attribute.range[0]
          : [attribute.range[0]]
        const max: number[] = Array.isArray(attribute.range[1])
          ? attribute.range[1]
          : [attribute.range[1]]

        const range_min = new THREE.Vector3(...min)
        const range_max = new THREE.Vector3(...max)
        const range = range_min.distanceTo(range_max)

        if (range === 0) {
          continue
        }

        selected = attributeName
        break
      }

      this.material.activeAttributeName = selected
    }

    {
      const intensityAttribute = this.pcoGeometry?.pointAttributes?.attributes.find(
        (attribute) =>
          attribute.name === 'intensity' &&
          Array.isArray(attribute.range) &&
          attribute.range.length === 2 &&
          Number.isFinite(attribute.range[0]) &&
          Number.isFinite(attribute.range[1])
      )

      if (intensityAttribute?.range) {
        let [min, max] = intensityAttribute.range

        if (min === max) {
          max = min + 1
        }

        const currentRange = this.material.intensityRange
        const needsDefaultRange =
          !Number.isFinite(currentRange[0]) ||
          !Number.isFinite(currentRange[1]) ||
          currentRange[0] >= currentRange[1]

        if (needsDefaultRange) {
          this.material.intensityRange = [min, max]
        }

        if ((this.material.activeAttributeName ?? '').includes('intensity')) {
          this.material.activeSamplingRange = [min, max]
        }
      }
    }

    this.name = ''

    {
      let box = [this.pcoGeometry.tightBoundingBox, this.getBoundingBoxWorld()].find(
        (v) => v !== undefined
      )

      this.updateMatrixWorld(true)
      box = computeTransformedBoundingBox(box!, this.matrixWorld)

      const bMin = box.min.z
      const bMax = box.max.z
      this.material.heightMin = bMin
      this.material.heightMax = bMax
    }

    // TODO read projection from file instead
    this.projection = geometry.projection
    this.fallbackProjection = geometry.fallbackProjection

    this.root = this.pcoGeometry.root
  }

  /**
   * 设置点云名称
   * @param name 新名称
   */
  setName(name: string): void {
    if (this.name !== name) {
      this.name = name
      this.dispatchEvent({ type: 'name_changed', name: name, pointcloud: this })
    }
  }

  /**
   * 获取点云名称
   * @returns 点云名称
   */
  getName(): string {
    return this.name
  }

  /**
   * 归一化属性名，便于大小写与分隔符不敏感地比较。
   *
   * @param name - 原始属性名
   * @returns 小写并去除 `-`、`_`、空格后的结果
   */
  getNormalizerName(name: string): string {
    return name.toLowerCase().replace(/[-_\s]/g, '') // 移除 -, _, 空格等分隔符
  }

  /**
   * 获取指定名称的点属性
   * @param name 属性名称
   * @returns 属性对象或 null
   */
  getAttribute(name: string): any {
    const attribute = this.pcoGeometry?.pointAttributes?.attributes.find(
      (a) =>
        this.getNormalizerName(this.getNormalizerName(a.name)) ===
        this.getNormalizerName(this.getNormalizerName(name))
    )

    if (attribute) {
      return attribute
    } else {
      return null
    }
  }

  /**
   * 获取所有点属性
   * @returns 点属性对象
   */
  getAttributes(): any {
    return this.pcoGeometry.pointAttributes
  }

  /**
   * 将几何节点转换为树节点
   * @param geometryNode 几何节点
   * @returns 树节点
   */
  toTreeNode(geometryNode: OctreeGeometryNode, _parent?: any): PointCloudOctreeNode {
    const existing = this.renderNodes.get(geometryNode)
    if (existing?.sceneNode && geometryNode.resourceState === 'resident') {
      return existing
    }
    if (!geometryNode.geometry || geometryNode.structural) {
      throw new Error(`节点 ${geometryNode.name} 没有可渲染驻留载荷`)
    }

    const node = new PointCloudOctreeNode()

    // if(geometryNode.name === "r40206"){
    //	console.log("creating node for r40206");
    // }
    const sceneNode = new THREE.Points(geometryNode.geometry, this.material)
    sceneNode.name = geometryNode.name
    sceneNode.position.copy(geometryNode.boundingBox.min)
    sceneNode.frustumCulled = false
    sceneNode.onBeforeRender = (renderer, _scene, _camera, _geometry, material: any, _group) => {
      if (material.program) {
        const gl = renderer.getContext()
        gl['useProgram'](material.program.program)

        if (material.program.getUniforms().map.level) {
          const level = geometryNode.getLevel()
          material.uniforms.level.value = level
          material.program.getUniforms().map.level.setValue(renderer.getContext(), level)
        }

        if (this.visibleNodeTextureOffsets && material.program.getUniforms().map.vnStart) {
          const vnStart = this.visibleNodeTextureOffsets.get(node)
          material.uniforms.vnStart.value = vnStart
          material.program.getUniforms().map.vnStart.setValue(gl, vnStart)
        }

        if (material.program.getUniforms().map.pcIndex) {
          const i = node.pcIndex ? node.pcIndex : this.visibleNodes.indexOf(node)
          material.uniforms.pcIndex.value = i
          material.program.getUniforms().map.pcIndex.setValue(gl, i)
        }
      }
    }

    // { // DEBUG
    // 	let sg = new THREE.SphereGeometry(1, 16, 16);
    // 	let sm = new THREE.MeshNormalMaterial();
    // 	let s = new THREE.Mesh(sg, sm);
    // 	s.scale.set(0.05, 0.05, 0.05);
    // 	console.log(geometryNode)
    // 	s.position
    // 		.add(this.position)
    // 		.add(geometryNode.boundingBox.min);

    // 	this.viewer.scene.scene.add(s);
    // }

    node.geometryNode = geometryNode
    node.sceneNode = sceneNode
    node.pointcloud = this
    node.children = []
    //for (let key in geometryNode.children) {
    //	node.children[key] = geometryNode.children[key];
    //}
    for (let i = 0; i < 8; i++) {
      node.children[i] = geometryNode.children[i]
    }

    this.add(sceneNode)
    this.renderNodes.set(geometryNode, node)
    geometryNode.markResident()

    const disposeListener = () => {
      node.sceneNode?.removeFromParent()
      if (node.sceneNode) {
        node.sceneNode.onBeforeRender = () => {}
      }
      node.sceneNode = null
      this.renderNodes.delete(geometryNode)
      const visibleIndex = this.visibleNodes.indexOf(node)
      if (visibleIndex >= 0) {
        this.visibleNodes.splice(visibleIndex, 1)
      }
    }
    geometryNode.oneTimeDisposeHandlers.push(disposeListener)

    return node
  }

  /** 返回几何节点当前的可渲染载荷包装；读取不会创建资源。 */
  getRenderNode(geometryNode: OctreeGeometryNode): PointCloudOctreeNode | null {
    return this.renderNodes.get(geometryNode) ?? null
  }

  /** 根据可见节点重新计算 visibleBounds，包含所有叶子节点的包围盒。 */
  updateVisibleBounds() {
    const leafNodes: any[] = []
    for (let i = 0; i < this.visibleNodes.length; i++) {
      const node = this.visibleNodes[i]
      let isLeaf = true

      for (let j = 0; j < node.children.length; j++) {
        const child = node.children[j]
        if (child instanceof PointCloudOctreeNode) {
          isLeaf = isLeaf && !child.sceneNode?.visible
        } else if (child instanceof OctreeGeometryNode) {
          isLeaf = true
        }
      }

      if (isLeaf) {
        leafNodes.push(node)
      }
    }

    this.visibleBounds.min = new THREE.Vector3(Infinity, Infinity, Infinity)
    this.visibleBounds.max = new THREE.Vector3(-Infinity, -Infinity, -Infinity)
    for (let i = 0; i < leafNodes.length; i++) {
      const node = leafNodes[i]

      this.visibleBounds.expandByPoint(node.getBoundingBox().min)
      this.visibleBounds.expandByPoint(node.getBoundingBox().max)
    }
  }

  /**
   * 更新材质参数，同步视口尺寸、相机 near/far 与八叉树尺寸。
   *
   * @param material - 目标材质对象
   * @param visibleNodes - 可见节点列表
   * @param camera - 活动相机，透视或正交
   * @param viewportSize - 当前 View 的逻辑像素尺寸
   */
  updateMaterial(
    material: PointCloudMaterial,
    _visibleNodes: any[],
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    viewportSize: { width: number; height: number }
  ): void {
    // @ts-expect-error 旧代码：OrthographicCamera 没有 fov 属性
    material.fov = camera.fov * (Math.PI / 180)
    material.screenWidth = viewportSize.width
    material.screenHeight = viewportSize.height
    material.spacing = this.pcoGeometry.spacing // * Math.max(this.scale.x, this.scale.y, this.scale.z);
    material.near = camera.near
    material.far = camera.far
    material.uniforms.octreeSize.value =
      this.pcoGeometry?.boundingBox?.getSize(new THREE.Vector3()).x ?? 0
  }

  /**
   * 生成供 shader 使用的可见节点纹理数据，描述八叉树层级结构。
   *
   * @param nodes - 当前可见节点集合
   * @param camera - 活动相机，用于子节点排序方向
   * @returns `{ data, offsets }`：纹理字节数组与节点到偏移的映射
   */
  computeVisibilityTextureData(nodes, _camera) {
    const data = new Uint8Array(nodes.length * 4)
    const visibleNodeTextureOffsets = new Map()

    // copy array
    nodes = nodes.slice()

    // sort by level and index, e.g. r, r0, r3, r4, r01, r07, r30, ...
    const sort = (a, b) => {
      const na = a.geometryNode.name
      const nb = b.geometryNode.name
      if (na.length !== nb.length) return na.length - nb.length
      if (na < nb) return -1
      if (na > nb) return 1
      return 0
    }
    nodes.sort(sort)

    const _worldDir = new THREE.Vector3()

    const nodeMap = new Map()
    const offsetsToChild = new Array(nodes.length).fill(Infinity)

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]

      nodeMap.set(node.name, node)
      visibleNodeTextureOffsets.set(node, i)
      if (i > 0) {
        const index = parseInt(node.name.slice(-1), 10)
        const parentName = node.name.slice(0, -1)
        const parent = nodeMap.get(parentName)
        const parentOffset = visibleNodeTextureOffsets.get(parent)

        const parentOffsetToChild = i - parentOffset

        offsetsToChild[parentOffset] = Math.min(offsetsToChild[parentOffset], parentOffsetToChild)

        data[parentOffset * 4 + 0] = data[parentOffset * 4 + 0] | (1 << index)
        data[parentOffset * 4 + 1] = offsetsToChild[parentOffset] >> 8
        data[parentOffset * 4 + 2] = offsetsToChild[parentOffset] % 256
      }

      const density = node.geometryNode.density

      if (typeof density === 'number' && !Number.isNaN(density)) {
        const lodOffset = Math.log2(density) / 2 - 1.5

        const offsetUint8 = (lodOffset + 10) * 10

        data[i * 4 + 3] = offsetUint8
      } else {
        data[i * 4 + 3] = 100
      }
    }

    return {
      data: data,
      offsets: visibleNodeTextureOffsets,
    }
  }

  /**
   * 判断节点是否与给定剖面的任意段相交。
   *
   * @param node - 候选八叉树节点
   * @param profile - 剖面定义，含折线点与宽度
   * @returns 与任意段相交返回 true
   */
  nodeIntersectsProfile(node, profile) {
    const bbWorld = node.boundingBox.clone().applyMatrix4(this.matrixWorld)
    const bsWorld = bbWorld.getBoundingSphere(new THREE.Sphere())

    let intersects = false

    for (let i = 0; i < profile.points.length - 1; i++) {
      const start = new THREE.Vector3(
        profile.points[i + 0].x,
        profile.points[i + 0].y,
        bsWorld.center.z
      )
      const end = new THREE.Vector3(
        profile.points[i + 1].x,
        profile.points[i + 1].y,
        bsWorld.center.z
      )

      const closest = new THREE.Line3(start, end).closestPointToPoint(
        bsWorld.center,
        true,
        new THREE.Vector3()
      )
      const distance = closest.distanceTo(bsWorld.center)

      intersects = intersects || distance < bsWorld.radius + profile.width
    }

    //console.log(`${node.name}: ${intersects}`);

    return intersects
  }

  /**
   * 查找包含指定世界坐标的最深节点。
   *
   * @param position - 世界坐标位置
   * @returns 包含该点的最深 PointCloudOctreeNode，无命中返回根节点
   */
  deepestNodeAt(position) {
    const toObjectSpace = this.matrixWorld.clone().invert()

    const objPos = position.clone().applyMatrix4(toObjectSpace)

    let current = this.root
    while (true) {
      let containingChild: any = null

      for (const child of current.children) {
        if (child !== undefined) {
          if (child.getBoundingBox().containsPoint(objPos)) {
            containingChild = child
          }
        }
      }

      if (containingChild !== null && containingChild instanceof PointCloudOctreeNode) {
        current = containingChild
      } else {
        break
      }
    }

    const deepest = current

    return deepest
  }

  /**
   * 筛选包围球与射线相交的节点。
   *
   * @param nodes - 候选节点集合
   * @param ray - 世界空间射线
   * @returns 与射线相交的节点子集
   */
  nodesOnRay(nodes, ray) {
    const nodesOnRay: any[] = []

    const _ray = ray.clone()
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]
      const sphere = node.getBoundingSphere().clone().applyMatrix4(this.matrixWorld)

      if (_ray.intersectsSphere(sphere)) {
        nodesOnRay.push(node)
      }
    }

    return nodesOnRay
  }

  /**
   * 更新世界矩阵，兼容 Three.js 行为。
   *
   * @param force - 为 true 时强制刷新子树
   */
  updateMatrixWorld(force?) {
    if (this.matrixAutoUpdate === true) this.updateMatrix()

    if (this.matrixWorldNeedsUpdate === true || force === true) {
      if (!this.parent) {
        this.matrixWorld.copy(this.matrix)
      } else {
        this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix)
      }

      this.matrixWorldNeedsUpdate = false

      force = true
    }
  }

  /**
   * 隐藏指定对象的所有后代 Object3D。
   *
   * @param object - 起始对象，对其所有可见后代设置 visible = false
   */
  hideDescendants(object) {
    const stack: any[] = []
    for (let i = 0; i < object.children.length; i++) {
      const child = object.children[i]
      if (child.visible) {
        stack.push(child)
      }
    }

    while (stack.length > 0) {
      const object = stack.shift()

      object.visible = false

      for (let i = 0; i < object.children.length; i++) {
        const child = object.children[i]
        if (child.visible) {
          stack.push(child)
        }
      }
    }
  }

  /** 将点云整体平移到世界原点（以世界包围盒中心对齐原点）。 */
  moveToOrigin() {
    this.position.set(0, 0, 0)
    this.updateMatrixWorld(true)
    const box = this.boundingBox
    const transform = this.matrixWorld
    const tBox = computeTransformedBoundingBox(box, transform)
    this.position.set(0, 0, 0).sub(tBox.getCenter(new THREE.Vector3()))
  }

  /** 将点云整体上移使世界包围盒底部贴合 y=0 平面。 */
  moveToGroundPlane() {
    this.updateMatrixWorld(true)
    const box = this.boundingBox
    const transform = this.matrixWorld
    const tBox = computeTransformedBoundingBox(box, transform)
    this.position.y += -tBox.min.y
  }

  /**
   * 获取点云在世界坐标系下的包围盒。
   *
   * @returns 经 matrixWorld 变换后的新 THREE.Box3
   */
  getBoundingBoxWorld() {
    this.updateMatrixWorld(true)
    const box = this.boundingBox
    const transform = this.matrixWorld
    const tBox = computeTransformedBoundingBox(box, transform)

    return tBox
  }

  /**
   * 获取当前可见叶子节点在世界坐标系下的外包范围。
   *
   * @returns 经 matrixWorld 变换后的可见范围包围盒
   */
  getVisibleExtent() {
    return this.visibleBounds.applyMatrix4(this.matrixWorld)
  }

  /**
   * 判断点云是否覆盖给定世界坐标（基于 SDF 栅格近似）。
   *
   * @param position - 世界坐标位置
   * @returns 该位置附近存在点返回 true
   */
  intersectsPoint(position) {
    const rootAvailable = this.pcoGeometry.root?.geometry

    if (!rootAvailable) {
      return false
    }

    if (typeof this.signedDistanceField === 'undefined') {
      const resolution = 32
      const field = new Float32Array(resolution ** 3).fill(Infinity)

      const positions = this.pcoGeometry.root!.geometry.attributes.position
      const boundingBox = this.boundingBox

      const n = positions.count

      for (let i = 0; i < n; i = i + 3) {
        const x = positions.array[3 * i + 0]
        const y = positions.array[3 * i + 1]
        const z = positions.array[3 * i + 2]

        const ix = parseInt(
          Math.min(resolution * (x / boundingBox.max.x), resolution - 1) as any,
          10
        )
        const iy = parseInt(
          Math.min(resolution * (y / boundingBox.max.y), resolution - 1) as any,
          10
        )
        const iz = parseInt(
          Math.min(resolution * (z / boundingBox.max.z), resolution - 1) as any,
          10
        )

        const index = ix + iy * resolution + iz * resolution * resolution

        field[index] = 0
      }

      const sdf = {
        resolution: resolution,
        field: field,
      }

      this.signedDistanceField = sdf
    }

    {
      const sdf = this.signedDistanceField
      const boundingBox = this.boundingBox

      const toObjectSpace = this.matrixWorld.clone().invert()

      const objPos = position.clone().applyMatrix4(toObjectSpace)

      const resolution = sdf.resolution
      const ix = parseInt((resolution * (objPos.x / boundingBox.max.x)) as any, 10)
      const iy = parseInt((resolution * (objPos.y / boundingBox.max.y)) as any, 10)
      const iz = parseInt((resolution * (objPos.z / boundingBox.max.z)) as any, 10)

      if (ix < 0 || iy < 0 || iz < 0) {
        return false
      }
      if (ix >= resolution || iy >= resolution || iz >= resolution) {
        return false
      }

      const index = ix + iy * resolution + iz * resolution * resolution

      const value = sdf.field[index]

      if (value === 0) {
        return true
      }
    }

    return false
  }

  /**
   * 在给定的像素位置拾取点云中的点，支持多种拾取模式。
   *
   * @param renderer - Three.js WebGL 渲染器
   * @param pRenderer - 点云渲染器，提供 renderOctree 接口
   * @param camera - 相机对象
   * @param ray - 拾取射线
   * @param params - 拾取参数，详见 PickParams
   * @returns 拾取到的点或点数组，未拾取到返回 null
   */
  pick(
    renderer: THREE.WebGLRenderer,
    pRenderer: { renderOctree(...args: any[]): void },
    camera: THREE.Camera,
    ray: THREE.Ray,
    params: PickParams = {}
  ): any {
    performance.mark('pick-start')

    const getVal = (a, b) => (a !== undefined ? a : b)

    const pickWindowSize = getVal(params.pickWindowSize, 65)
    const _pickOutsideClipRegion = getVal(params.pickOutsideClipRegion, false)

    const size = renderer.getSize(new THREE.Vector2())

    const width = Math.ceil(getVal(params.width, size.width))
    const height = Math.ceil(getVal(params.height, size.height))

    const pointSizeType = getVal(params.pointSizeType, this.material.pointSizeType)
    const pointSize = getVal(params.pointSize, this.material.size)

    const nodes = this.nodesOnRay(this.visibleNodes, ray)

    if (nodes.length === 0) {
      return null
    }

    if (!this.pickState) {
      const scene = new THREE.Scene()

      const material = new PointCloudMaterial()
      material.activeAttributeName = 'indices'

      const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
      })

      this.pickState = {
        renderTarget: renderTarget,
        material: material,
        scene: scene,
      }
    }

    const pickState = this.pickState
    const pickMaterial = pickState.material
    pickMaterial.pointSizeType = pointSizeType
    //pickMaterial.shape = this.material.shape;
    pickMaterial.shape = PointShape.PARABOLOID

    pickMaterial.uniforms.uFilterReturnNumberRange.value =
      this.material.uniforms.uFilterReturnNumberRange.value
    pickMaterial.uniforms.uFilterNumberOfReturnsRange.value =
      this.material.uniforms.uFilterNumberOfReturnsRange.value
    pickMaterial.uniforms.uFilterGPSTimeClipRange.value =
      this.material.uniforms.uFilterGPSTimeClipRange.value
    pickMaterial.uniforms.uFilterPointSourceIDClipRange.value =
      this.material.uniforms.uFilterPointSourceIDClipRange.value

    pickMaterial.activeAttributeName = 'indices'

    pickMaterial.size = pointSize
    pickMaterial.uniforms.minSize.value = this.material.uniforms.minSize.value
    pickMaterial.uniforms.maxSize.value = this.material.uniforms.maxSize.value
    pickMaterial.classification = this.material.classification
    pickMaterial.recomputeClassification()

    const pickMinDistanceEnabled = params.minDistance !== undefined
    pickMaterial.uniforms.uPickMinDistanceEnabled.value = pickMinDistanceEnabled
    pickMaterial.uniforms.uPickMinDistance.value = params.minDistance ?? 0
    if (pickMinDistanceEnabled) {
      camera.updateMatrixWorld(true)
      const cameraPosition = camera.getWorldPosition(new THREE.Vector3())
      const inverseCameraRotation = camera.getWorldQuaternion(new THREE.Quaternion()).invert()
      pickMaterial.uniforms.uPickRayOriginView.value
        .copy(ray.origin)
        .sub(cameraPosition)
        .applyQuaternion(inverseCameraRotation)
      pickMaterial.uniforms.uPickRayDirectionView.value
        .copy(ray.direction)
        .normalize()
        .applyQuaternion(inverseCameraRotation)
        .normalize()
    } else {
      pickMaterial.uniforms.uPickRayOriginView.value.set(0, 0, 0)
      pickMaterial.uniforms.uPickRayDirectionView.value.set(0, 0, -1)
    }

    if (params.pickClipped) {
      pickMaterial.clipBoxes = this.material.clipBoxes
      pickMaterial.uniforms.clipBoxes = this.material.uniforms.clipBoxes
      if (this.material.clipTask === ClipTask.HIGHLIGHT) {
        pickMaterial.clipTask = ClipTask.NONE
      } else {
        pickMaterial.clipTask = this.material.clipTask
      }
      pickMaterial.clipMethod = this.material.clipMethod
    } else {
      pickMaterial.clipBoxes = []
    }

    // @ts-expect-error 旧代码：Camera 类型不匹配 PerspectiveCamera | OrthographicCamera
    this.updateMaterial(pickMaterial, nodes, camera, { width, height })

    pickState.renderTarget.setSize(width, height)

    const pixelPos = new THREE.Vector2(params.x, params.y)

    const gl = renderer.getContext() as WebGL2RenderingContext
    gl.enable(gl.SCISSOR_TEST)
    gl.scissor(
      parseInt((pixelPos.x - (pickWindowSize - 1) / 2) as any, 10),
      parseInt((pixelPos.y - (pickWindowSize - 1) / 2) as any, 10),
      parseInt(pickWindowSize, 10),
      parseInt(pickWindowSize, 10)
    )

    renderer.state.buffers.depth.setTest(pickMaterial.depthTest)
    renderer.state.buffers.depth.setMask(pickMaterial.depthWrite)
    renderer.state.setBlending(THREE.NoBlending)

    {
      // RENDER
      renderer.setRenderTarget(pickState.renderTarget)
      gl.clearColor(0, 0, 0, 0)
      renderer.clear(true, true, true)

      const tmp = this.material
      this.material = pickMaterial

      pRenderer.renderOctree(
        this,
        nodes,
        camera as THREE.PerspectiveCamera | THREE.OrthographicCamera,
        pickState.renderTarget
      )

      this.material = tmp
    }

    const clamp = (number, min, max) => Math.min(Math.max(min, number), max)

    const x = parseInt(clamp(pixelPos.x - (pickWindowSize - 1) / 2, 0, width) as any, 10)
    const y = parseInt(clamp(pixelPos.y - (pickWindowSize - 1) / 2, 0, height) as any, 10)
    const _w = parseInt((Math.min(x + pickWindowSize, width) - x) as any, 10)
    const _h = parseInt((Math.min(y + pickWindowSize, height) - y) as any, 10)

    // let pixelCount = w * h;
    const pixelCount = pickWindowSize * pickWindowSize
    const buffer = new Uint8Array(4 * pixelCount)

    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
    gl.readPixels(x, y, pickWindowSize, pickWindowSize, gl.RGBA, gl.UNSIGNED_BYTE, buffer)

    // renderer.setRenderTarget(null);
    // renderer.state.reset();
    renderer.setScissorTest(false)
    gl.disable(gl.SCISSOR_TEST)

    const pixels = buffer
    const ibuffer = new Uint32Array(buffer.buffer)

    // find closest hit inside pixelWindow boundaries
    const _min = Number.MAX_VALUE
    const hits: any[] = []
    for (let u = 0; u < pickWindowSize; u++) {
      for (let v = 0; v < pickWindowSize; v++) {
        const offset = u + v * pickWindowSize
        const distance = (u - (pickWindowSize - 1) / 2) ** 2 + (v - (pickWindowSize - 1) / 2) ** 2

        const pcIndex = pixels[4 * offset + 3]
        pixels[4 * offset + 3] = 0
        const pIndex = ibuffer[offset]

        if (!(pcIndex === 0 && pIndex === 0) && pcIndex !== undefined && pIndex !== undefined) {
          const hit = {
            pIndex: pIndex,
            pcIndex: pcIndex - 1,
            distanceToCenter: distance,
          }

          if (params.all) {
            hits.push(hit)
          } else {
            if (hits.length > 0) {
              if (distance < hits[0].distanceToCenter) {
                hits[0] = hit
              }
            } else {
              hits.push(hit)
            }
          }
        }
      }
    }

    // if(params.debug){ // DEBUG: show panel with pick image
    // 	try {
    // 		let img = pixelsArrayToImage(buffer, w, h);
    // 		let screenshot = img.src;

    // 		if(!this.debugDIV){
    // 			this.debugDIV = $(`
    // 				<div id="pickDebug"
    // 				style="position: absolute;
    // 				right: 400px; width: 300px;
    // 				bottom: 44px; width: 300px;
    // 				z-index: 1000;
    // 				"></div>`);
    // 			$(document.body).append(this.debugDIV);
    // 		}

    // 		this.debugDIV.empty();
    // 		this.debugDIV.append($(`<img src="${screenshot}"
    // 			style="transform: scaleY(-1); width: 300px"/>`));
    // 		//$(this.debugWindow.document).append($(`<img src="${screenshot}"/>`));
    // 		//this.debugWindow.document.write('<img src="'+screenshot+'"/>');
    // 	} catch (error) {
    // 		console.error(error);

    // 	}
    // }

    for (const hit of hits) {
      const point: Record<string, any> = {}

      if (!nodes[hit.pcIndex]) {
        return null
      }

      const node = nodes[hit.pcIndex]
      const pc = node.sceneNode
      const geometry = node.geometryNode.geometry
      point.__node = node

      for (const attributeName in geometry.attributes) {
        const attribute = geometry.attributes[attributeName]

        if (attributeName === 'position') {
          const x = attribute.array[3 * hit.pIndex + 0]
          const y = attribute.array[3 * hit.pIndex + 1]
          const z = attribute.array[3 * hit.pIndex + 2]

          const position = new THREE.Vector3(x, y, z)
          position.applyMatrix4(pc.matrixWorld)

          point[attributeName] = position
        } else if (attributeName === 'indices') {
        } else {
          let values = attribute.array.slice(
            attribute.itemSize * hit.pIndex,
            attribute.itemSize * (hit.pIndex + 1)
          )

          if (attribute.potree) {
            const { scale, offset } = attribute.potree
            values = values.map((v) => v / scale + offset)
          }

          point[attributeName] = values

          //debugger;
          //if (values.itemSize === 1) {
          //	point[attribute.name] = values.array[hit.pIndex];
          //} else {
          //	let value = [];
          //	for (let j = 0; j < values.itemSize; j++) {
          //		value.push(values.array[values.itemSize * hit.pIndex + j]);
          //	}
          //	point[attribute.name] = value;
          //}
        }
      }

      hit.point = point
    }

    performance.mark('pick-end')
    performance.measure('pick', 'pick-start', 'pick-end')

    if (params.all) {
      return hits.map((hit) => hit.point)
    } else {
      if (hits.length === 0) {
        return null
      } else {
        return hits[0].point
        //let sorted = hits.sort( (a, b) => a.distanceToCenter - b.distanceToCenter);

        //return sorted[0].point;
      }
    }
  }

  /**
   * Loader 资产加载生命周期进度，取值 0~1。
   *
   * PointCloudOctree 仅在 Loader 成功完成后创建，因此可观察时固定为 1。
   * 加载期间的中间值由 `asset.progress` 事件提供，与视图 LOD 状态无关。
   */
  get progress(): number {
    return 1
  }

  /**
   * 当前视图候选 LOD 节点的就绪比例，取值 0~1。
   *
   * 该值不保证单调；多视口时取当前帧所有已计算可见视口的最小值。
   */
  get viewLodProgress(): number {
    return this._viewLodProgress
  }

  /**
   * 记录一次视口可见性遍历的 LOD 就绪度。
   *
   * @param readyNodes - 候选集合中已经可渲染的 TreeNode 数
   * @param totalNodes - 同一候选集合的节点总数
   * @param context - 可选帧与视口上下文；省略时按单视口结果覆盖
   */
  updateViewLodProgress(
    readyNodes: number,
    totalNodes: number,
    context: ViewLodProgressContext = {}
  ): void {
    const ratio = totalNodes === 0 ? 1 : readyNodes / totalNodes
    const normalized = Number.isFinite(ratio) ? Math.min(1, Math.max(0, ratio)) : 0
    const { frameNumber, viewId, visibleViewIds } = context

    if (frameNumber === undefined || viewId === undefined) {
      this._viewLodProgressFrameNumber = -1
      this._viewLodProgressByView.clear()
      this._viewLodProgressByView.set('__single__', normalized)
      this._viewLodProgress = normalized
      return
    }

    if (this._viewLodProgressFrameNumber !== frameNumber) {
      this._viewLodProgressFrameNumber = frameNumber
      this._viewLodProgressByView.clear()
    }

    if (visibleViewIds) {
      const visibleViews = new Set(visibleViewIds)
      for (const storedViewId of this._viewLodProgressByView.keys()) {
        if (!visibleViews.has(storedViewId)) {
          this._viewLodProgressByView.delete(storedViewId)
        }
      }
    }

    this._viewLodProgressByView.set(viewId, normalized)
    this._viewLodProgress = Math.min(...this._viewLodProgressByView.values())
  }

  /**
   * 按节点路径名查找节点（如 "r073"）。
   *
   * @param name - 以 r 开头的节点路径
   * @returns 命中节点；路径不存在抛异常或返回 undefined
   */
  find(name) {
    let node: any = null
    for (const char of name) {
      if (char === 'r') {
        node = this.root
      } else {
        node = node.children[char]
      }
    }

    return node
  }

  /** 点云可见性开关，set 时派发 visibility_changed 事件 */
  // @ts-expect-error
  get visible() {
    return this._visible
  }

  set visible(value) {
    if (value !== this._visible) {
      this._visible = value

      this.dispatchEvent({ type: 'visibility_changed', pointcloud: this })
    }
  }

  /**
   * 遍历所有子孙 Object3D，对每个节点调用 handler。
   *
   * @param handler - 访问回调，接收当前节点
   */
  traverseDescendants(handler) {
    for (const child of this.children) {
      child.traverse(handler)
    }
  }
  /**
   * 深度释放八叉树几何节点（含子节点）。
   *
   * @param item - 起始几何节点
   */
  traverseRelease(item) {
    const arr = Object.values(item.children)
    item.dispose(false)
    if (!arr.length) return
    arr.forEach((a) => {
      this.traverseRelease(a)
    })
  }
  /** 释放点云相关资源，包含几何、材质、渲染目标与八叉树节点。 */
  dispose() {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    if (this.pcoGeometry) {
      this.pcoGeometry.loadSignal = undefined
      this.pcoGeometry.loader?.dispose()
      this.pcoGeometry.dispose()
    }

    // 释放子节点的几何体和材质资源
    this.traverseDescendants((child) => {
      if (child.geometry) {
        child.geometry.dispose()
      }
      if (child.material) {
        child.material.dispose()
      }
    })

    // 移除事件监听器
    if (this.root?.sceneNode) {
      this.root.sceneNode.onBeforeRender = null
    }

    // 释放渲染目标
    if (this.pickState?.renderTarget) {
      this.pickState.renderTarget.dispose()
    }
    if (this.pickState?.material) {
      this.pickState.material.dispose()
    }
    this.pickState = undefined

    this.material.dispose()

    // 清理数据结构
    this.visibleNodes = []
    this._viewLodProgressByView.clear()
    this._viewLodProgress = 0
    this.loadQueue = []
    this.profileRequests = []
    this.renderNodes = new WeakMap()
  }
}
